import React, { useEffect, useState } from 'react';
import { Gift, Loader2, CheckCircle, XCircle, Users, Share2, Calendar, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AmbassadorStatusCard = ({ variant = 'full' }) => {
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/promotions/ambassador/info`);
      setInfo(res.data);
    } catch (e) {
      // Silently fail for non-auth users
      setInfo(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markShared = async () => {
    try {
      await axios.post(`${API}/promotions/ambassador/mark-shared`);
      toast.success('Marked as shared.');
      load();
    } catch (e) {
      toast.error('Failed to update.');
    }
  };

  const checkUnlock = async () => {
    try {
      const res = await axios.post(`${API}/promotions/ambassador/check-unlock`);
      if (res.data.status === 'unlocked') toast.success('Unlocked 12 months Pro. Congrats!');
      else toast.message('Not unlocked yet. Complete tasks to unlock 12 months.');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Unlock check failed');
    }
  };

  if (loading) {
    return (
      <div className=" border border-slate-800 bg-slate-900/40 p-5">
        <div className="flex items-center gap-3 text-slate-300">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading ambassador status…
        </div>
      </div>
    );
  }

  if (!info?.promo) return null;

  const tasks = info.tasks || {};
  const doneCount = Object.values(tasks).filter((t) => t?.done).length;
  const totalCount = Object.keys(tasks).length || 0;

  const claimed = !!info.user?.ambassador_claimed;
  const active = !!info.user?.ambassador_access_active;
  const verified = !!info.user?.email_verified;

  const deadline = info.user?.ambassador_task_deadline ? new Date(info.user.ambassador_task_deadline) : null;

  return (
    <div className=" border border-slate-800 bg-slate-900/40 p-6" data-testid="ambassador-status-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11  bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center">
            <Gift className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-[var(--isp-heading)] font-bold">Ambassador Pass</h3>
            <p className="text-sm text-slate-400">
              First 1000 verified signups. Complete tasks to unlock 12 months.
            </p>
          </div>
        </div>

        <div className={`text-xs font-semibold px-2.5 py-1 rounded-lg border ${active ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-[var(--isp-glass)] backdrop-blur-xl text-slate-300 border-slate-700'}`}>
          {active ? 'Pro Active' : 'Not Active'}
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat label="Spots remaining" value={info.promo.remaining} />
        <Stat label="Progress" value={`${doneCount}/${totalCount}`} />
        <Stat label="Verified" value={verified ? 'Yes' : 'No'} icon={verified ? ShieldCheck : XCircle} />
      </div>

      {claimed && deadline ? (
        <div className="mt-4 flex items-center gap-2 text-sm text-slate-300">
          <Calendar className="w-4 h-4 text-slate-400" />
          Task deadline: <span className="font-semibold">{deadline.toLocaleDateString()}</span>
        </div>
      ) : null}

      {variant === 'full' && (
        <div className="mt-5 space-y-3">
          <TaskLine label="Verify email" done={tasks.email_verified?.done} />
          <TaskLine label="Complete profile" done={tasks.profile_complete?.done} />
          <TaskLine label="Create a study asset" done={tasks.created_study_asset?.done} />
          <TaskLine
            label="Share on social"
            done={tasks.shared_social?.done}
            action={!tasks.shared_social?.done ? (
              <button
                type="button"
                onClick={markShared}
                className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 transition-colors text-white"
              >
                <span className="inline-flex items-center gap-1"><Share2 className="w-3.5 h-3.5" /> I shared</span>
              </button>
            ) : null}
          />
          <TaskLine
            label={`Referral signups (${tasks.referrals?.count || 0}/${tasks.referrals?.target || 10})`}
            done={tasks.referrals?.done}
            icon={Users}
          />
        </div>
      )}

      {claimed && (
        <div className="mt-5 flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={checkUnlock}
            className="flex-1 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-[var(--isp-heading)] font-semibold py-2.5 transition-colors"
          >
            Check unlock (12 months)
          </button>
        </div>
      )}
    </div>
  );
};

const Stat = ({ label, value, icon: Icon }) => (
  <div className="rounded-xl border border-slate-800 bg-slate-950/40 p-4">
    <p className="text-xs text-slate-500">{label}</p>
    <div className="mt-1 flex items-center gap-2">
      {Icon ? <Icon className="w-4 h-4 text-slate-400" /> : null}
      <p className="text-lg font-bold text-white">{value ?? '—'}</p>
    </div>
  </div>
);

const TaskLine = ({ label, done, icon: Icon = null, action = null }) => (
  <div className="flex items-center justify-between gap-3">
    <div className="flex items-center gap-2 text-sm">
      {Icon ? <Icon className="w-4 h-4 text-slate-400" /> : null}
      {done ? (
        <CheckCircle className="w-4 h-4 text-green-400" />
      ) : (
        <XCircle className="w-4 h-4 text-slate-600" />
      )}
      <span className={done ? 'text-slate-200' : 'text-slate-400'}>{label}</span>
    </div>
    {action}
  </div>
);

export default AmbassadorStatusCard;
